/**
 * Which squares a clue is talking about.
 *
 * Clue segments already carry what they refer to — a room, an object, a person, a line — because
 * the renderer builds them from structured atoms rather than prose. This turns one of those
 * references into the squares to light up, which is what makes a clue something you can point at
 * rather than only read.
 */

import { kindAt } from "@engine/atoms";
import type { SegmentRef } from "@engine/render";
import type { Board, CellIdx } from "@engine/types";

export function cellsForRef(
  ref: SegmentRef,
  board: Board,
  placements: ReadonlyArray<CellIdx | null>,
): CellIdx[] {
  const size = board.size;
  const every = Array.from({ length: size * size }, (_, cell) => cell);

  switch (ref.type) {
    case "room":
      return every.filter((cell) => board.regions[cell] === ref.roomId);
    case "object":
      return every.filter((cell) => kindAt(board, cell) === ref.objectKind);
    case "person": {
      // Only useful once they are on the board; before that there is nothing to point at.
      const cell = placements[ref.char];
      return cell === null || cell === undefined ? [] : [cell];
    }
    case "row":
      return every.filter((cell) => Math.floor(cell / size) === ref.index);
    case "col":
      return every.filter((cell) => cell % size === ref.index);
  }
}
